import React, { useState, useEffect } from "react";
import LiteAPI from "../api/liteAPI";
import { Typography, Row, Col, Button, Progress, Tag } from "antd";
import {
  MonitorPlay,
  ArrowRight,
  FolderOpen,
  ListMusic,
  Zap,
  HardDrive,
} from "lucide-react";
import { motion } from "framer-motion";
import { useLanguage, UI_TRANSLATIONS } from "../LanguageContext";

const { Title, Text } = Typography;

const stagger = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 24 },
  },
};

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return "0 MB";
  const gb = bytes / (1024 * 1024 * 1024);
  if (gb >= 1) return `${gb.toFixed(2)} GB`;
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
};

const Overview = ({
  onNavigate,
  hasScreen,
  screenData,
  libraryCount = 0,
  playlistCount = 0,
  apiProps,
  brandData,
}) => {
  const [storageData, setStorageData] = useState(null);
  const [fbStatus, setFbStatus] = useState({});
  const { lang } = useLanguage();
  const t = UI_TRANSLATIONS[lang].overview;

  // Subscribe to Firebase real-time device status for the first paired screen
  useEffect(() => {
    if (!screenData?.raw) return;
    setFbStatus({});
    const unsubscribe = LiteAPI.subscribeScreenStatus(
      screenData.raw,
      (data) => {
        setFbStatus(data);
      },
    );
    return unsubscribe;
  }, [screenData?.raw?.ScreenCode]);

  useEffect(() => {
    if (!apiProps || !brandData?.BrandID) return;
    LiteAPI.getAccountStorage(brandData.BrandID, apiProps.usertoken)
      .then((data) => {
        if (data) setStorageData(data);
      })
      .catch((err) => console.warn("[Overview] Storage fetch failed:", err));
  }, [brandData?.BrandID]);

  const storageUsed = storageData?.storagebyte?.usesapce ?? 0;
  const storageTotal = storageData?.storagebyte?.totalspace || 1073741824; // default 1 GB if null/undefined/0
  const storagePercent = storageData?.storagebyte?.percentuse ?? 0;

  return (
    <motion.div variants={stagger} initial="hidden" animate="show">
      {/* ── Welcome Header ── */}
      <motion.div variants={fadeUp} style={{ marginBottom: "36px" }}>
        <Title
          level={2}
          style={{
            color: "#0f172a",
            margin: 0,
            fontWeight: 700,
            letterSpacing: "-0.02em",
          }}
        >
          {t.welcome}{" "}
          <span className="lite-gradient-text">{t.welcomeSuffix}</span>{" "}
          {t.welcomeEnd}
        </Title>
        <Text style={{ fontSize: "15px", color: "#64748b" }}>{t.subtitle}</Text>
      </motion.div>

      {/* ── Quick Stats Row ── */}
      <Row
        gutter={[20, 20]}
        style={{ marginBottom: "32px", alignItems: "stretch" }}
      >
        {/* Screen Status */}
        <Col xs={24} md={8} style={{ display: "flex" }}>
          <motion.div
            variants={fadeUp}
            id="lite-stat-display"
            className="lite-card clickable"
            onClick={() => onNavigate("screen")}
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "14px",
                marginBottom: "16px",
              }}
            >
              <div className="lite-stat-icon purple">
                <MonitorPlay size={22} color="#6366f1" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  className="lite-card-title"
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {t.displayCard}
                </div>
                <div
                  className="lite-card-subtitle"
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {hasScreen
                    ? screenData?.ScreenName || t.connected
                    : t.noPaired}
                </div>
              </div>
            </div>
            {console.log("fbStatus", fbStatus)}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <Tag
                  color={fbStatus?.is_online === 1 ? "#10b981" : "default"}
                  className={fbStatus?.is_online === 1 ? "lite-tag-online" : ""}
                  style={{
                    margin: 0,
                    borderRadius: "12px",
                    fontSize: "12px",
                    padding: "2px 10px",
                    flexShrink: 0,
                  }}
                >
                  {fbStatus?.is_online === 1 ? t.online : t.offline}
                </Tag>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {hasScreen ? t.slotUsed : t.tapToPair}
                </Text>
              </div>
              <ArrowRight size={16} color="#94a3b8" />
            </div>
          </motion.div>
        </Col>

        {/* Content Library */}
        <Col xs={24} md={8} style={{ display: "flex" }}>
          <motion.div
            variants={fadeUp}
            id="lite-stat-library"
            className="lite-card clickable"
            onClick={() => onNavigate("library")}
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "14px",
                marginBottom: "16px",
              }}
            >
              <div className="lite-stat-icon blue">
                <FolderOpen size={22} color="#3b82f6" />
              </div>
              <div style={{ flex: 1 }}>
                <div className="lite-card-title">{t.libraryCard}</div>
                <div className="lite-card-subtitle">
                  {t.mediaFiles(libraryCount)}
                </div>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Text type="secondary" style={{ fontSize: "12px" }}>
                {t.manageContent}
              </Text>
              <ArrowRight size={16} color="#94a3b8" />
            </div>
          </motion.div>
        </Col>

        {/* Active Playlist */}
        <Col xs={24} md={8} style={{ display: "flex" }}>
          <motion.div
            variants={fadeUp}
            id="lite-stat-playlist"
            className="lite-card clickable"
            onClick={() => onNavigate("playlist")}
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "14px",
                marginBottom: "16px",
              }}
            >
              <div className="lite-stat-icon green">
                <ListMusic size={22} color="#10b981" />
              </div>
              <div style={{ flex: 1 }}>
                <div className="lite-card-title">{t.playlistCard}</div>
                <div className="lite-card-subtitle">
                  {t.itemsInLoop(playlistCount)}
                </div>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Text type="secondary" style={{ fontSize: "12px" }}>
                {t.reorderPublish}
              </Text>
              <ArrowRight size={16} color="#94a3b8" />
            </div>
          </motion.div>
        </Col>
      </Row>

      {/* ── Bottom Row ── */}
      <Row gutter={[20, 20]}>
        {/* Getting Started / Quick Actions */}
        <Col xs={24} lg={14}>
          <motion.div variants={fadeUp}>
            <Title
              level={4}
              style={{
                marginBottom: "16px",
                fontWeight: 600,
                color: "#0f172a",
              }}
            >
              {t.getStarted}
            </Title>
            <div
              id="lite-quick-actions"
              className="lite-card no-hover"
              style={{ padding: 0, overflow: "hidden" }}
            >
              {[
                {
                  icon: <MonitorPlay size={20} color="#6366f1" />,
                  title: hasScreen ? t.manageDisplay : t.connectDisplay,
                  subtitle: hasScreen
                    ? t.manageDisplaySub
                    : t.connectDisplaySub,
                  action: () => onNavigate("screen"),
                  done: hasScreen,
                },
                {
                  icon: <FolderOpen size={20} color="#3b82f6" />,
                  title: libraryCount > 0 ? t.uploadMore : t.uploadFirst,
                  subtitle:
                    libraryCount > 0 ? t.filesReady(libraryCount) : t.uploadSub,
                  action: () => onNavigate("library"),
                  done: libraryCount > 0,
                },
                {
                  icon: <ListMusic size={20} color="#10b981" />,
                  title: playlistCount > 0 ? t.editPublish : t.createPlaylist,
                  subtitle:
                    playlistCount > 0
                      ? t.itemsReady(playlistCount)
                      : t.arrangeSub,
                  action: () => onNavigate("playlist"),
                  done: playlistCount > 0,
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  onClick={item.action}
                  style={{
                    padding: "18px 24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: idx < 2 ? "1px solid #f1f5f9" : "none",
                    cursor: "pointer",
                    transition: "background 0.15s ease",
                    background: item.done ? "#f0fdf4" : "#ffffff",
                    borderLeft: item.done
                      ? "3px solid #10b981"
                      : "3px solid transparent",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = item.done
                      ? "#e6f9ee"
                      : "#f8fafc")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = item.done
                      ? "#f0fdf4"
                      : "#ffffff")
                  }
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                    }}
                  >
                    <div
                      style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "10px",
                        background: item.done
                          ? "linear-gradient(135deg, #10b981, #059669)"
                          : "#f1f5f9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: item.done
                          ? "0 2px 8px rgba(16, 185, 129, 0.3)"
                          : "none",
                      }}
                    >
                      {item.done ? (
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#ffffff"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        item.icon
                      )}
                    </div>
                    <div>
                      <Text
                        strong
                        style={{
                          display: "block",
                          fontSize: "14px",
                          color: item.done ? "#065f46" : "#0f172a",
                        }}
                      >
                        {item.title}
                      </Text>
                      <Text
                        style={{
                          fontSize: "12px",
                          color: item.done ? "#10b981" : "#94a3b8",
                        }}
                      >
                        {item.done ? t.completed : item.subtitle}
                      </Text>
                    </div>
                  </div>
                  <ArrowRight
                    size={16}
                    color={item.done ? "#10b981" : "#94a3b8"}
                  />
                </div>
              ))}
            </div>
          </motion.div>
        </Col>

        {/* Storage & Tips */}
        <Col xs={24} lg={10}>
          <motion.div variants={fadeUp}>
            <Title
              level={4}
              style={{
                marginBottom: "16px",
                fontWeight: 600,
                color: "#0f172a",
              }}
            >
              {t.usage}
            </Title>

            <div className="lite-card" style={{ marginBottom: "16px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "12px",
                }}
              >
                <HardDrive size={18} color="#10b981" />
                <Text strong>{t.storage}</Text>
              </div>
              <Progress
                percent={storagePercent}
                strokeColor={{ from: "#6366f1", to: "#8b5cf6" }}
                trailColor="#f1f5f9"
                showInfo={false}
                strokeWidth={8}
                style={{ marginBottom: "8px" }}
              />
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {formatBytes(storageUsed)} {t.used}
                </Text>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {formatBytes(storageTotal)} {t.limit}
                </Text>
              </div>
            </div>

            <div
              className="lite-card"
              style={{
                background: "linear-gradient(135deg, #312e81 0%, #1e1b4b 100%)",
                border: "none",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginBottom: "12px",
                }}
              >
                <Zap size={18} color="#fbbf24" />
                <Text strong style={{ color: "#ffffff" }}>
                  {t.proTip}
                </Text>
              </div>
              <Text
                style={{
                  color: "rgba(255, 255, 255, 0.75)",
                  fontSize: "13px",
                  display: "block",
                  marginBottom: "16px",
                  lineHeight: "1.6",
                }}
              >
                {t.proTipText}
              </Text>
              <Button
                size="small"
                className="lite-goto-library-btn"
                onClick={() => onNavigate("library")}
                style={{
                  background: "rgba(255,255,255,0.15)",
                  border: "none",
                  color: "#ffffff",
                  fontWeight: 500,
                  borderRadius: "8px",
                }}
              >
                {t.goToLibrary}
              </Button>
            </div>
          </motion.div>
        </Col>
      </Row>
    </motion.div>
  );
};

export default Overview;
